import styled, {css, keyframes} from 'styled-components';

import {AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR} from '../constants/AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR';
import {withAlphaHex} from '../withAlphaHex';

export type AiComposerSurfaceVariant = 'popover' | 'bar' | 'toolbar';

interface AiComposerSurfaceProps {
  $variant: AiComposerSurfaceVariant;
}

const enter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.985);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

// Hex alpha suffixes: 0.92, 0.94, 0.82, 0.75 and 0.08 of the theme colour.
const ALPHA_SHEET_TOP = 'EB';
const ALPHA_SHEET_BOTTOM = 'F0';
const ALPHA_GLASS = 'D1';
const ALPHA_LINE = 'BF';
const ALPHA_SHADOW_SOFT = '14';
const HAIRLINE = '1px';

export const AiComposerSurface = styled.div<AiComposerSurfaceProps>`
  position: relative;
  min-width: 0;
  border-radius: ${({theme}) => theme.radii.medium};
  box-shadow:
    inset 0 0 0 ${HAIRLINE}
      ${({theme}) => withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_LINE)},
    0 ${({theme}) => theme.getSize(2)} ${({theme}) => theme.getSize(5)}
      ${({theme}) => withAlphaHex(theme.colors.palettes.neutral[900][100], ALPHA_SHADOW_SOFT)};
  -webkit-backdrop-filter: blur(${({theme}) => theme.getSize(4)}) saturate(1.3);
  backdrop-filter: blur(${({theme}) => theme.getSize(4)}) saturate(1.3);
  animation: ${enter} 180ms cubic-bezier(0.2, 0.9, 0.25, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${({$variant, theme}) =>
    $variant === 'popover' &&
    css`
      display: flex;
      flex-direction: column;
      max-height: var(${AI_COMPOSER_POPOVER_MAX_HEIGHT_VAR}, none);
      padding: ${theme.getSize(3)};
      background: linear-gradient(
        180deg,
        ${withAlphaHex(theme.colors.palettes.purple[20][100], ALPHA_SHEET_TOP)},
        ${withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_SHEET_BOTTOM)}
      );
      box-shadow: inset 0 0 0 ${HAIRLINE}
        ${withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_LINE)};
    `}

  ${({$variant, theme}) =>
    $variant === 'bar' &&
    css`
      padding: ${theme.getSize(2)} ${theme.getSize(3)};
      background: ${withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_GLASS)};
    `}

  ${({$variant, theme}) =>
    $variant === 'toolbar' &&
    css`
      display: inline-flex;
      align-items: center;
      padding: ${theme.getSize(1)};
      background: ${withAlphaHex(theme.colors.palettes.white[10][100], ALPHA_SHEET_BOTTOM)};
    `}
`;
